import {
  computed,
  Directive,
  effect,
  EffectRef,
  EventEmitter,
  inject,
  InjectionToken,
  OnDestroy,
  Output,
} from '@angular/core';
import {ControlService, Debouncer, IApiControl, IFilter} from 'frontier/nucleus';
import {
  distinctUntilChanged,
  finalize,
  isObservable,
  Observable,
  of,
  pipe,
  Subject,
  Subscription,
  switchMap,
  timer,
} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';
import {toObservable} from '@angular/core/rxjs-interop';
import {ControlStore} from 'frontier/nucleus/src/lib/apiv2/control.store';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {tapResponse} from '@ngrx/operators';
import {patchState, signalState} from '@ngrx/signals';

export type IInitialStateProps = {
  filter: IFilter;
  sorting: object;
  custom: object;
  enableTenantTracking: boolean;
}

export
interface IGroupMap { [key: string]: number[] }

type ControlState<TDto, TModel> = IInitialStateProps & {
  apiInstance: IApiControl | null;
  isInitialized: boolean;
  isChanged: boolean;
  isDeleted: boolean;
  data: TModel;
  apiData: TDto;
  loading: boolean;
  enableTenantTracking: boolean;
}

export const INITIAL_STATE_TOKEN = new InjectionToken<Partial<IInitialStateProps>>('initialState');

// Structural comparison for filter/sorting/custom payloads. They are sent as JSON to the api,
// so JSON equality is exactly "the backend would receive the same request".
function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

@Directive()
export abstract class SignalControl<TDto, TModel = TDto> implements OnDestroy {
  initialState: Partial<IInitialStateProps> = inject(INITIAL_STATE_TOKEN, {optional: true});
  GUID: string;

  groupMap: IGroupMap; // All indices in the array are assigned to the name of the key when used as a vertical table or
  // with the EDIT_ACTION

  protected readonly controlService: ControlService = inject(ControlService);
  protected readonly controlStore: ControlStore = inject(ControlStore);
  protected readonly debouncer: Debouncer = new Debouncer();

  readonly state = signalState<ControlState<TDto, TModel>>({
    apiInstance: null,
    filter: {},
    sorting: {},
    custom: {},
    isInitialized: false,
    isChanged: false,
    isDeleted: false,
    data: null,
    apiData: null,
    loading: true,
    enableTenantTracking: false,
    ...this.initialState
  });
  readonly apiInstance = this.state.apiInstance;
  readonly apiInstance$ = toObservable(this.apiInstance);
  readonly instanceId = computed(() => this.apiInstance()?.instanceid);
  readonly filter = this.state.filter;
  readonly sorting = this.state.sorting;
  readonly custom = this.state.custom;
  readonly isInitialized = this.state.isInitialized;
  readonly isInitialized$ = toObservable(this.isInitialized).pipe(
    filter(isInitialized => !!isInitialized),
  )
  readonly isChanged = this.state.isChanged;
  readonly isDeleted = this.state.isDeleted;
  readonly data = this.state.data;
  readonly apiData = this.state.apiData;
  readonly loading = this.state.loading;
  readonly enableTenantTracking = this.state.enableTenantTracking;

  protected subs = new Subscription();

  private changeInstanceParams = computed(() => {
    return {
      filter: this.filter(),
      sorting: this.sorting(),
      custom: this.custom(),
      isInitialized: this.isInitialized()
    }
  })

  loading$ = toObservable<boolean>(this.loading);

  // Show loading indicator with debounce only when loading is true
  protected readonly showLoadingIndicator$ = this.loading$.pipe(
    switchMap(isLoading => {
      // When loading becomes true, delay showing the indicator
      if (isLoading) {
        return timer(2000).pipe(map(() => isLoading));
      }
      // When loading becomes false, immediately hide the indicator
      return of(isLoading);
    }),
    distinctUntilChanged()
  );

  @Output() instanceInitialized = new EventEmitter<IApiControl>();

  tenantChanged$ = new Subject<null>();

  toModel?(data: TDto): TModel | Observable<TModel> {
    return data as unknown as TModel;
  };

  // abstract toDto(data: TModel): TDto;

  createInstanceEffectRef: EffectRef;

  constructor() {
    this.createInstanceEffectRef = effect(() => {
      const filter = this.filter();
      const sorting = this.sorting();
      const custom = this.custom();
      if (filter && sorting && custom) {
        console.log('createInstance triggered')
        this.createInstance().subscribe();
        this.createInstanceEffectRef.destroy();
      }
    }, {manualCleanup: true});

    // Derived channel: refetch when filter/sorting/custom actually change by value. Explicit
    // changeAndFetchInstance() calls (refresh commands, e.g. polling) bypass this and always run.
    this.changeAndFetchInstance(
      toObservable(this.changeInstanceParams).pipe(
        distinctUntilChanged(jsonEqual)
      )
    )

    if (this.enableTenantTracking()) {
      effect((): void => {
        const tenant: string = this.controlStore.tenant()
        if (this.GUID && tenant) {
          const currentTenant: string = localStorage.getItem(this.GUID);
          if (currentTenant !== tenant) {
            localStorage.setItem(this.GUID, tenant);
            if (jsonEqual(this.filter(), {})) {
              // Filter reset is a no-op for the derived channel, but the new tenant still
              // requires a refetch — issue it as an explicit command.
              this.changeAndFetchInstance();
            } else {
              patchState((this.state), _ => ({filter: {}}))
            }
            this.tenantChanged$.next(null)
          }
        }
      }, {allowSignalWrites: true});
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.deleteInstance();
    this.createInstanceEffectRef?.destroy();
  }

  private createInstance() {
    console.log('Creating instance ...', this.GUID);
    return this.controlService.controlPostInstance({
      _parameters: [
        this.GUID, this.filter(), this.sorting(), this.custom()
      ]
    }).pipe(
      tapResponse({
        next: (apiInstance) => {
          console.log("API Instance created", apiInstance);
          patchState(this.state, _ => ({
            apiInstance: apiInstance as IApiControl,
            isInitialized: true,
          }))
          this.instanceInitialized.emit(apiInstance as IApiControl);
        },
        error: error => {
          console.error(error);
        }
      })
    )
  }

  readonly changeAndFetchInstance = rxMethod<void | Partial<{
    instanceId: string,
    filter: IFilter,
    sorting: object,
    custom: object,
  }>>(
    pipe(
      filter(params => {
        console.log("Change and fetch instance", params);
        return this.isInitialized()
      }),
      tap((params) => {
        patchState(this.state, _ => ({loading: true}));
        console.log('Changing instance ...', params)
      }),
      switchMap(partialParams => {
        if (!partialParams) {
          partialParams = {};
        }
        const params = {
          instanceId: partialParams?.instanceId || this.apiInstance().instanceid,
          filter: partialParams?.filter || this.filter(),
          sorting: partialParams?.sorting || this.sorting(),
          custom: partialParams?.custom || this.custom(),
        }
        return this.queueRequest(
          this.controlService.controlPostChangeInstanceAndFetch({
            _parameters: [
              params.instanceId, params.filter, params.sorting, params.custom
            ]
          }).pipe(
            tap(apiData => patchState(this.state, _ => ({apiData: apiData as TDto}))),
            switchMap(dto => {
              const mapped = this.toModel(dto as TDto);
              if (isObservable(mapped)) {
                return mapped.pipe(map(v => ({data: v, apiData: dto})));
              } else {
                return of({data: mapped, apiData: dto})
              }
            }),
            tapResponse({
              next: (result) => {
                console.log("API Instance changed", result);
                patchState(this.state, _ => ({
                  data: result.data as TModel,
                  isChanged: true,
                  loading: false
                }))
              },
              error: error => {
                console.error(error);
              }
            }),
            catchError(_ => {
              patchState(this.state, _ => ({loading: false}));
              return of(null);
            })
          )
        )
      })
    )
  )

  private deleteInstance() {
    if (this.apiInstance()?.instanceid == null) {
      return;
    }
    patchState(this.state, _ => ({loading: true}));
    this.queueRequest(
      this.controlService.controlDeleteInstance(this.apiInstance().instanceid).pipe(
        finalize(() => {
          patchState(this.state, _ => ({
            apiInstance: null,
            isDeleted: true,
            loading: false
          }))
        })
      )).subscribe()
  }

  /**
   * Method to prevent multiple api requests that trigger database transaction, possibly leading to an "open current transaction error"
   * @param apiRequest
   */
  protected queueRequest(apiRequest: Observable<any>): Observable<any> {
    return this.debouncer.queueRequest(apiRequest);
  }

  patchFilter(patch: IFilter): void {
    const merged: IFilter = {...this.filter(), ...patch};
    if (jsonEqual(merged, this.filter())) {
      return;
    }
    console.log('PatchFilter triggered')
    patchState(this.state, _ => ({filter: merged}));
  }

  setSorting(sorting: object): void {
    if (jsonEqual(sorting, this.sorting())) {
      return;
    }
    patchState(this.state, _ => ({sorting}));
  }

  setCustom(custom: object): void {
    if (jsonEqual(custom, this.custom())) {
      return;
    }
    patchState(this.state, _ => ({custom}));
  }
}
